<template>
  <v-container fluid>
    <v-card outlined elevation="1">
      <v-toolbar color="primary" dark dense flat>
        <v-toolbar-title>Edit Completion Report</v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn color="white" small text @click="printReport(reportId)">
          <v-icon left>mdi-printer</v-icon> Print
        </v-btn>

        <!-- Copies the saved report; unsaved edits on this page are not carried over. -->
        <v-btn color="white" small text @click="duplicate">
          <v-icon left>mdi-content-duplicate</v-icon> Duplicate
        </v-btn>

        <v-btn color="white" small text :to="'/completion-reports'">
          <v-icon left>mdi-arrow-left</v-icon> Back
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-form ref="formRef">
          <v-row dense>
            <!-- Ref No -->
            <v-col cols="12" md="6">
              <v-text-field disabled outlined hide-details dense label="Reference No" v-model="inputs.ref_no" />
            </v-col>

            <!-- Date -->
            <v-col cols="12" md="6">
              <v-text-field outlined hide-details dense type="date" label="Date" v-model="inputs.date" />
            </v-col>

            <!-- Customer -->
            <v-col cols="12" md="6">
              <v-autocomplete outlined hide-details dense label="Customer" item-value="id"
                :item-text="item => `${item.id} - ${item.name}`" :items="customers" v-model="inputs.customer_id" />
            </v-col>

            <!-- Project Name -->
            <v-col cols="12" md="6">
              <v-text-field outlined hide-details dense label="Project Name" v-model="inputs.project_name" />
            </v-col>

            <!-- Description -->
            <v-col cols="12">
              <v-textarea outlined hide-details dense label="Description" rows="3" auto-grow
                v-model="inputs.description" />
            </v-col>

            <!-- Scope of Works -->
            <v-col cols="12">
              <label class="font-weight-bold mb-2 d-block">Scope of Works</label>
              <div v-for="(item, index) in inputs.scope_of_works" :key="'scope-' + index"
                class="d-flex align-center mb-2">
                <v-text-field v-model="inputs.scope_of_works[index]" outlined dense class="flex-grow-1 mr-2"
                  placeholder="Enter scope item" />
                <v-btn icon color="red" @click="inputs.scope_of_works.splice(index, 1)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
              <v-btn small color="primary" @click="inputs.scope_of_works.push('')">
                <v-icon left>mdi-plus</v-icon> Add Scope Item
              </v-btn>
            </v-col>

            <!-- Work Completed -->
            <v-col cols="12">
              <label class="font-weight-bold mb-2 d-block">Work Completed</label>
              <div v-for="(item, index) in inputs.work_completed" :key="'completed-' + index"
                class="d-flex align-center mb-2">
                <v-text-field v-model="inputs.work_completed[index]" outlined dense class="flex-grow-1 mr-2"
                  placeholder="Enter completed work item" />
                <v-btn icon color="red" @click="inputs.work_completed.splice(index, 1)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
              <v-btn small color="primary" @click="inputs.work_completed.push('')">
                <v-icon left>mdi-plus</v-icon> Add Completed Item
              </v-btn>
            </v-col>

            <!-- Conclusion -->
            <v-col cols="12">
              <v-textarea outlined hide-details dense label="Conclusion" rows="3" auto-grow
                v-model="inputs.conclusion" />
            </v-col>

            <!-- IMAGES -->
            <v-col cols="12">
              <div class="text-subtitle-2 font-weight-bold mb-2">Images</div>

              <!-- Loop through computed imageRows -->
              <v-row dense v-for="(row, rowIndex) in imageRows" :key="'row-' + rowIndex" align="start">
                <v-col v-for="(img, i) in row" :key="'img-' + rowIndex + '-' + i"
                  :md="Math.floor(spanToNumber(img.span) * 12)">
                  <div class="d-flex flex-column" style="gap:10px">
                    <!-- Image Name -->
                    <v-text-field outlined dense hide-details label="Image Name" v-model="img.name" />

                    <!-- Span Selector -->
                    <v-select outlined dense hide-details label="Span" :items="spanOptions" item-text="label"
                      item-value="value" v-model="img.span" />

                    <!-- File Upload -->
                    <v-file-input outlined dense hide-details prepend-inner-icon="mdi-image" accept="image/*"
                      @change="onFileChange($event, img)" />

                    <!-- Preview -->
                    <v-img v-if="img.preview" :src="img.preview" max-height="200" class="mt-2" contain />
                  </div>

                  <!-- Remove Button -->
                  <v-btn icon small color="red" class="mt-1" @click="removeImage(img)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Add Image Button -->
              <v-btn small color="blue darken-1" dark class="mt-2" @click="addImage">
                <v-icon left>mdi-plus</v-icon> Add Image
              </v-btn>
            </v-col>
          </v-row>

          <!-- Buttons -->
          <v-divider class="my-4"></v-divider>

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-btn block color="primary" dark @click="updateReport">
                <v-icon left>mdi-content-save</v-icon> Update
              </v-btn>
            </v-col>

            <v-col cols="12" sm="6">
              <v-btn block color="blue darken-2" dark :to="'/completion-reports'">
                <v-icon left>mdi-file-document</v-icon> Reports
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import appAxios from '@/libs/app-axios'
import { LoadingPopup, SuccessPopup, ErrorPopup } from '@/libs/utils'
import { isAxiosError } from 'axios'

export default {
  name: 'EditCompletionReport',
  data() {
    return {
      inputs: {
        ref_no: '',
        project_name: '',
        description: '',
        date: '',
        customer_id: '',
        scope_of_works: [],
        work_completed: [],
        conclusion: '',
        images: [], // NEW: array of image objects
      },
      customers: [],
      files: {},
      preview: {},
      spanOptions: [
        { label: '1/2', value: '1/2' },
        { label: '1/3', value: '1/3' },
        { label: '1', value: '1' },
      ],
      errors: {},
    }
  },
  computed: {
    reportId() {
      return this.$route.params.completionReportId
    },
    imageRows() {
      const rows = []
      let currentRow = []
      let currentSpan = 0

      this.inputs.images.forEach(img => {
        const span = this.spanToNumber(img.span) || 0.5
        if (currentSpan + span > 1) {
          rows.push(currentRow)
          currentRow = []
          currentSpan = 0
        }
        currentRow.push(img)
        currentSpan += span
      })
      if (currentRow.length) rows.push(currentRow)
      return rows
    },
  },
  mounted() {
    this.fetchCustomers()
    this.fetchReport()
  },
  methods: {
    async fetchCustomers() {
      try {
        const res = await appAxios().get('/customers')
        this.customers = res.data
      } catch (e) {
        ErrorPopup.fire()
      }
    },
    async fetchReport() {
      try {
        LoadingPopup.fire('Loading...')
        const { data } = await appAxios().get(`/v3/completion-reports/${this.reportId}/edit`)
        this.inputs = {
          ref_no: data.ref_no,
          project_name: data.project_name,
          description: data.description,
          date: data.date,
          customer_id: data.customer_id,
          scope_of_works: data.scope_of_works.split('\n'),
          work_completed: data.work_completed.split('\n'),
          conclusion: data.conclusion,
          images: data.images.map(img => ({
            id: img.id,
            name: img.name,
            span: img.span,
            preview: img.preview || null,
            file: null,
          })),
        }
        LoadingPopup.close()
      } catch (e) {
        ErrorPopup.fire('Failed to load report')
      }
    },
    async updateReport() {
      try {
        const form = new FormData()
        form.append('_method', 'PUT')
        form.append('project_name', this.inputs.project_name)
        form.append('description', this.inputs.description)
        form.append('date', this.inputs.date)
        form.append('customer_id', this.inputs.customer_id)
        form.append('conclusion', this.inputs.conclusion)

        this.inputs.scope_of_works.forEach(s => form.append('scope_of_works[]', s))
        this.inputs.work_completed.forEach(w => form.append('work_completed[]', w))

        // Append images
        this.inputs.images.forEach((img, i) => {
          form.append(`images[${i}][name]`, img.name)
          form.append(`images[${i}][span]`, img.span)
          if (img.file) form.append(`images[${i}][file]`, img.file)
          if (img.id) form.append(`images[${i}][id]`, img.id)
        })

        LoadingPopup.fire('Updating...')
        await appAxios().post(`/v3/completion-reports/${this.reportId}`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        LoadingPopup.close()
        SuccessPopup.fire('Report updated!')
      } catch (err) {
        LoadingPopup.close()
        if (isAxiosError(err) && err.response?.status === 422) {
          this.errors = err.response.data.errors
          return
        }
        ErrorPopup.fire('Failed to update')
      }
    },
    async printReport(id) {
      try {
        const { data } = await appAxios().get(`/v3/completion-reports/${id}/pdf`, { responseType: 'blob' })
        const fileURL = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
        window.open(fileURL, '_blank')
      } catch (e) {
        ErrorPopup.fire('Failed to generate PDF')
      }
    },
    spanToNumber(span) {
      if (span === '1') return 1
      if (span === '1/2') return 0.5
      if (span === '1/3') return 1 / 3
      return 0.5
    },

    onFileChange(file, img) {
      img.file = file
      img.preview = file ? URL.createObjectURL(file) : img.preview
    },

    addImage() {
      this.inputs.images.push({ name: '', span: '1/2', file: null, preview: null })
    },

    removeImage(img) {
      const index = this.inputs.images.indexOf(img)
      if (index > -1) this.inputs.images.splice(index, 1)
    },

    duplicate() {
      this.$router.push({
        name: 'CompletionReports.Create',
        query: { duplicateFrom: this.reportId },
      })
    },
  },
}
</script>